import { createContext, useContext } from 'react';
import { CUSTOM_AGENTS_BASE } from './customAgentsRoutes';

/**
 * How each contract names the Custom Agents module (transition report §12.6).
 * These are different contracts that happen to describe the same product, so
 * they sit side by side rather than collapsing into one string — never
 * globally replace one with another.
 */
export const CUSTOM_AGENTS_MODULE = Object.freeze({
  /** Product key in productConfig and the sidebar product switcher. */
  productKey: 'custom-agents',
  /** Prefix of the canonical routes. */
  routeBase: CUSTOM_AGENTS_BASE,
  /** Graph / Agent Atlas surface id (AgentNetwork `surface`, Atlas surface switchers). */
  graphSurface: 'custom',
  /**
   * Security Atlas surface key — what the producers write for customers' own
   * OpenTelemetry-instrumented agents (`surface:otel` in rules, incidents and
   * filters). SurfaceScope takes this value, but only once each Atlas endpoint
   * has been verified to honour it (§12.4).
   */
  securitySurface: 'otel',
  // Backend alert-module values for Custom Agents live in the surface
  // manifest (custom.yaml, ids.alert_modules), served by Atlas /surfaces.
  /** Digest module key the shared pages read from AgentLabels. */
  digestModule: 'monitoring',
});

/**
 * Which shared-page sections the Custom Agents backend can actually serve
 * (§12.3). A false entry means the section is left out with an intentional
 * layout — never rendered as zeros, sample data, or a "No data" state that
 * implies collection is running. Flip an entry only once its contract has been
 * validated against real tenant data.
 */
export const CUSTOM_AGENTS_CAPABILITIES = Object.freeze({
  intentAnalysis: false,     // shared page wired; backend readiness pending (§7.5.9)
  recommendations: true,     // engine module `custom_agents` + 9 rules (2026-10-03)
  knowledgeGaps: false,      // needs the normalized gap classifier (§7.5.8)
  grounding: false,          // no source/citation telemetry in the Custom contract (§7.5.10)
  productivityValue: false,  // no documented hours/value formula (§9.2)
  adoptionMetrics: false,    // adoption contract not validated (§9.2)
  sessionDetails: true,      // Log Tracer list/details normalized for the shared shell (§8.3)
  responseActions: false,    // no execution/approval contract (§7.5.17)
  accessGraph: false,        // Access Management contract pending (§7.5.19)
});

export const hasCapability = (name, capabilities = CUSTOM_AGENTS_CAPABILITIES) => capabilities[name] === true;

/**
 * Labels for the shared (Gemini-built) pages when they render for Custom
 * Agents. No license fields: Custom Agents economics are token and
 * infrastructure based, not per seat (§7.5.6).
 */
export const CUSTOM_AGENTS_LABELS = Object.freeze({
  module: CUSTOM_AGENTS_MODULE.digestModule,
  noun: 'Agent',
  nounPlural: 'Agents',
  pageTitle: 'Conversations',
  pageSubtitle: 'Explore sessions and conversations with your custom AI agents',
  totalStatHelp: 'Total number of conversations across all custom agents',
  totalStatTrend: 'All agents combined',
  unknownLabel: 'Unknown',
  responseSenderFallback: 'Agent',
  hasAgentFilter: true,
  hasContent: true,
  routeBase: CUSTOM_AGENTS_BASE,
  usersPageTitle: 'Identities',
  usersPageSubtitle: 'Monitor custom agent users, activity, and risk',
  usersStatHelp: 'Total number of unique users who have interacted with custom agents.',
  userDetailMode: 'risk',
  showConversationCompletion: false,
  showConversationFlow: false,
  showConversationComplexity: false,
  showGrounding: false,
  showProductivityValue: false,
  showAdoptionSegments: false,
});

const CustomAgentsModuleContext = createContext(null);

export const CustomAgentsModuleProvider = CustomAgentsModuleContext.Provider;

/** Module identity and capabilities, for pages under CustomAgentsLayout. */
export function useCustomAgentsModule() {
  const context = useContext(CustomAgentsModuleContext);
  if (!context) throw new Error('useCustomAgentsModule must be used within CustomAgentsLayout');
  return context;
}
